import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import { NodeSelection } from "@tiptap/pm/state";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";

// Custom FontSize Extension
import { Mark } from "@tiptap/core";
const FontSize = Mark.create({
  name: "fontSize",
  addAttributes() {
    return {
      size: {
        default: null,
        parseHTML: (element) => element.style.fontSize || null,
        renderHTML: (attributes) => {
          if (!attributes.size) return {};
          return { style: `font-size: ${attributes.size}` }; // Corrected template literal
        },
      },
    };
  },
  parseHTML() {
    return [{ style: "font-size" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },
  addCommands() {
    return {
      setFontSize:
        (size) =>
        ({ commands }) => {
          return commands.setMark(this.name, { size });
        },
    };
  },
});

// Custom Heading-like Mark Extension for inline styling
const HeadingStyle = Mark.create({
  name: "headingStyle",
  addAttributes() {
    return {
      level: {
        default: 1,
        parseHTML: (element) => element.getAttribute("data-heading-level") || 1,
        renderHTML: (attributes) => {
          if (!attributes.level) return {};
          const fontSizes = {
            1: "2em",
            2: "1.5em",
            3: "1.17em",
          };
          return {
            style: `font-size: ${
              fontSizes[attributes.level]
            }; font-weight: bold;`, // Corrected template literal
            "data-heading-level": attributes.level,
          };
        },
      },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-heading-level]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },
  addCommands() {
    return {
      setHeadingStyle:
        (level) =>
        ({ commands }) => {
          return commands.setMark(this.name, { level });
        },
      toggleHeadingStyle:
        (level) =>
        ({ commands }) => {
          return commands.toggleMark(this.name, { level });
        },
    };
  },
});

// Custom FontFamily Extension
const FontFamily = Mark.create({
  name: "fontFamily",
  addAttributes() {
    return {
      family: {
        default: null,
        parseHTML: (element) => element.style.fontFamily || null,
        renderHTML: (attributes) => {
          if (!attributes.family) return {};
          return { style: `font-family: ${attributes.family}` }; // Corrected template literal
        },
      },
    };
  },
  parseHTML() {
    return [{ style: "font-family" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },
  addCommands() {
    return {
      setFontFamily:
        (family) =>
        ({ commands }) => {
          return commands.setMark(this.name, { family });
        },
    };
  },
});

// Custom Background Color Extension (to ensure highlighting works properly)
// Renamed from BackgroundColor to CustomHighlight to avoid conflict/confusion
// with the goal of setting the *default* background color.
const CustomHighlight = Mark.create({
  name: "customHighlight", // Changed name
  addAttributes() {
    return {
      color: {
        default: null,
        parseHTML: (element) => element.style.backgroundColor || null,
        renderHTML: (attributes) => {
          if (!attributes.color) return {};
          return { style: `background-color: ${attributes.color}` }; // Corrected template literal
        },
      },
    };
  },
  parseHTML() {
    return [{ style: "background-color" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },
  addCommands() {
    return {
      setCustomHighlight:
        (color) =>
        ({ commands }) => {
          // Changed command name
          return commands.setMark(this.name, { color });
        },
      unsetCustomHighlight:
        () =>
        ({ commands }) => {
          // Changed command name
          return commands.unsetMark(this.name);
        },
    };
  },
});

// Add global styles through a style element
const addEditorStyles = () => {
  // Check if the style element already exists
  if (!document.getElementById("tiptap-editor-styles")) {
    const styleEl = document.createElement("style");
    styleEl.id = "tiptap-editor-styles";
    styleEl.innerHTML = `
      .ProseMirror {
        word-wrap: break-word !important;
        white-space: pre-wrap !important;
        white-space: break-spaces !important;
        overflow-wrap: break-word !important;
        max-width: 100% !important;
        outline: none;
      }

      .ProseMirror p {
        word-wrap: break-word !important;
        word-break: break-word !important;
        overflow-wrap: break-word !important;
        margin: 0.5em 0;
      }

      .ProseMirror ul, .ProseMirror ol {
        padding-left: 1.5em;
      }

      /* Style for default highlighting (if not using custom highlight) */
      .ProseMirror mark {
        background-color: var(--highlight-color, #ffff8d);
        color: inherit;
      }

      /* Style for custom background color applied as a mark */
      .ProseMirror span[style*="background-color:"] {
         border-radius: 2px; /* Optional: makes highlighted text look nicer */
      }

      /* Style for inline heading styles */
      .ProseMirror span[data-heading-level="1"] {
        font-size: 2em;
        font-weight: bold;
      }

      .ProseMirror span[data-heading-level="2"] {
        font-size: 1.5em;
        font-weight: bold;
      }

      .ProseMirror span[data-heading-level="3"] {
        font-size: 1.17em;
        font-weight: bold;
      }

      .editor-toolbar {
        display: flex;
        flex-wrap: wrap;
        gap: 2px;
        padding: 5px;
        background-color: #f8f9fa;
        border: 1px solid #e2e8f0;
        border-bottom: none;
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
      }

      .toolbar-button {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 28px;
        height: 28px;
        border-radius: 4px;
        border: 1px solid transparent;
        background: transparent;
        cursor: pointer;
        padding: 0 8px;
        color: #4a5568;
        font-size: 14px;
        font-weight: 500;
      }

      .toolbar-button:hover {
        background-color: #edf2f7;
      }

      .toolbar-button.active {
        background-color: #e2e8f0;
        border-color: #cbd5e0;
      }

      .toolbar-divider {
        width: 1px;
        height: 24px;
        background-color: #e2e8f0;
        margin: 0 4px;
      }

      .dropdown {
        position: relative;
        display: inline-block;
      }

      .dropdown-content {
        display: none;
        position: absolute;
        background-color: #fff;
        min-width: 160px;
        max-height: 220px;
        overflow: auto;
        box-shadow: 0 8px 16px rgba(0,0,0,0.1);
        z-index: 1;
        border-radius: 4px;
        border: 1px solid #e2e8f0;
      }

      .dropdown-content button {
        width: 100%;
        text-align: left;
        padding: 8px 16px;
        border: none;
        background: transparent;
        cursor: pointer;
      }

      .dropdown-content button:hover {
        background-color: #f7fafc;
      }

      /* Show dropdown on hover */
      .dropdown:hover .dropdown-content {
        display: block;
      }

      /* Always show dropdown when active class is present */
      .dropdown.active .dropdown-content {
        display: block;
      }

      .color-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 4px;
        margin: 2px;
        cursor: pointer;
        border: 1px solid rgba(0,0,0,0.1);
      }

      .color-button:hover {
        transform: scale(1.1);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }

      .color-picker {
        display: flex;
        flex-wrap: wrap;
        width: 210px;
        padding: 4px;
      }

      .editor-content {
        border: 1px solid #e2e8f0;
        border-radius: 0 0 4px 4px;
        padding: 16px;
        min-height: 150px;
        overflow: auto;
        width: 100%;
        font-size: 16px;
        line-height: 1.5;
        color: #333; /* Default text color */
      }

      /* Color indicator on toolbar buttons */
      .color-indicator {
        width: 14px;
        height: 14px;
        border-radius: 2px;
        margin-right: 4px;
        border: 1px solid rgba(0,0,0,0.1);
      }

      /* Close button for dropdowns */
      .dropdown-close {
        position: absolute;
        top: 5px;
        right: 5px;
        cursor: pointer;
        font-size: 12px;
        color: #666;
      }

      /* Status indicator for current selection */
      .format-status {
        display: flex;
        align-items: center;
        background: #f1f5f9;
        padding: 4px 8px;
        border-radius: 4px;
        margin-left: auto;
        font-size: 12px;
        color: #64748b;
      }
    `;
    document.head.appendChild(styleEl);
  }
};

const fontOptions = [
  "Arial",
  "Times New Roman",
  "Courier New",
  "Georgia",
  "Verdana",
  "Tahoma",
];

const fontSizes = [
  "8",
  "9",
  "10",
  "11",
  "12",
  "14",
  "16",
  "18",
  "20",
  "22",
  "24",
  "28",
  "36",
  "48",
  "72",
];

const colorOptions = [
  "#000000",
  "#FF0000",
  "#0000FF",
  "#00FF00",
  "#FFFF00",
  "#FF00FF",
  "#00FFFF",
  "#808080",
  "#800000",
  "#008000",
  "#000080",
  "#808000",
  "#800080",
  "#008080",
  "#FFFFFF",
  "#FCF4A3",
  "#FF9966",
  "#99CCFF",
  "#1A1A1A",
  "#333333",
  "#4B0082",
  "#8B0000",
  "#00008B",
  "#006400",
  "#2F4F4F",
  "#8B4513",
  "#483D8B",
  "#F0F0F0",
  "#FFD700",
  "#F5DEB3",
  "#FFB6C1",
  "#ADD8E6",
  "#90EE90",
  "#E6E6FA",
  "#FFFFE0",
  "#D3D3D3",
  "#FFA07A",
  "#FAFAD2",
];

const TipTapEditor = ({ value, onChange, isGrayMode }) => {
  // Added isGrayMode prop
  const [selectedFont, setSelectedFont] = useState("Arial");
  const [selectedFontSize, setSelectedFontSize] = useState("16");
  const [selectedParagraph, setSelectedParagraph] = useState("Paragraph");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentTextColor, setCurrentTextColor] = useState("#000000");
  const [currentHighlightColor, setCurrentHighlightColor] = useState("#FFFF00");

  // Generate a stable unique ID for this instance
  const [instanceId] = useState(
    () => `tiptap-${Math.random().toString(36).substr(2, 9)}`
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      // Configure Highlight to work with colors properly
      Highlight.configure({
        multicolor: true,
      }),
      CustomHighlight, // Use our custom highlight extension
      FontSize,
      FontFamily,
      HeadingStyle, // Add our custom heading-style extension
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Link.configure({
        openOnClick: false,
      }),
      Image,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
    onSelectionUpdate: ({ editor }) => {
      // Update selected formatting based on current selection
      updateCurrentFormatting(editor);
    },
  });

  // Update UI based on current selection formatting
  const updateCurrentFormatting = (editor) => {
    if (!editor) return;

    // Check for text color
    const color = editor.getAttributes("textStyle").color;
    if (color) setCurrentTextColor(color);
    else {
      // If no specific color is applied, use the default (which might be white in gray mode)
      // We can't easily read the computed CSS color here, so we'll rely on the initial state
      // or you might need more complex logic to determine the effective color.
      // For simplicity, we'll just set it to black when no specific color is found,
      // as the white will be applied by CSS.
      setCurrentTextColor("#000000");
    }

    // Check for highlight color
    const highlight = editor.getAttributes("customHighlight").color; // Check our custom highlight
    if (highlight) setCurrentHighlightColor(highlight);
    else {
      // If no custom highlight, check for the default highlight extension if used
      const defaultHighlight = editor.getAttributes("highlight").color;
      if (defaultHighlight) {
        setCurrentHighlightColor(defaultHighlight);
      } else {
        // Default highlight color when none is applied
        setCurrentHighlightColor("#FFFF00");
      }
    }

    // Check for font family
    const family = editor.getAttributes("fontFamily").family;
    if (family) setSelectedFont(family);
    else setSelectedFont("Arial"); // Default font

    // Check for font size
    const size = editor.getAttributes("fontSize").size;
    if (size) {
      // Extract numeric value from size (e.g., "16px" -> "16")
      const sizeValue = size.replace("px", "");
      setSelectedFontSize(sizeValue);
    } else setSelectedFontSize("16"); // Default size

    // Check for paragraph style (basic check, can be expanded)
    if (editor.isActive("paragraph")) {
      setSelectedParagraph("Paragraph");
    } else if (editor.isActive("headingStyle", { level: 1 })) {
      setSelectedParagraph("Heading 1");
    } else if (editor.isActive("headingStyle", { level: 2 })) {
      setSelectedParagraph("Heading 2");
    } else if (editor.isActive("headingStyle", { level: 3 })) {
      setSelectedParagraph("Heading 3");
    } else {
      setSelectedParagraph("Paragraph"); // Default
    }
  };

  // Effect for general editor setup and content
  useEffect(() => {
    // Add the editor styles when component mounts
    addEditorStyles();

    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }
  }, [editor, value]); // Dependencies

  // Effect for instance-specific gray mode styles
  useEffect(() => {
    if (isGrayMode && instanceId) {
      const styleElId = `${instanceId}-gray-mode-styles`;
      if (!document.getElementById(styleElId)) {
        const styleEl = document.createElement("style");
        styleEl.id = styleElId;
        styleEl.innerHTML = `
                .${instanceId} .editor-content {
                    background-color: #b2b2b2; /* Gray background */
                    caret-color: white;
                }
                .${instanceId} .ProseMirror {
                    color: #FFFFFF !important; /* White text, !important to override defaults */
                }
                /* Adjust selection colors for readability */
                .${instanceId} ::selection {
                    background-color: #cccccc; /* Lighter gray selection background */
                    color: #000000; /* Black text on selection */
                }
                /* For Firefox */
                .${instanceId} ::-moz-selection {
                    background-color: #cccccc;
                    color: #000000;
                }
                /* Ensure link colors are visible in gray mode */
                 .${instanceId} .ProseMirror a {
                    color: #ADD8E6 !important; /* Light blue for links */
                }
                /* Ensure list markers are visible */
                 .${instanceId} .ProseMirror ul, .${instanceId} .ProseMirror ol {
                    color: #FFFFFF; /* List markers inherit color */
                 }
            `;
        document.head.appendChild(styleEl);
      }

      // Cleanup function to remove the injected styles
      return () => {
        const styleEl = document.getElementById(styleElId);
        if (styleEl) {
          styleEl.remove();
        }
      };
    }
    // If isGrayMode is false, and there were styles, they will be cleaned up
    // by the return function of the previous effect run.
    // No cleanup needed if not in gray mode.
    return () => {};
  }, [isGrayMode, instanceId]); // Dependencies: re-run if isGrayMode or instanceId changes

  // Add a method to set node selection without overriding chain
  const setNodeSelection = (pos) => {
    if (!editor) return;

    try {
      const { state, view } = editor;
      const resolvedPos = state.doc.resolve(pos);
      const nodeSelection = NodeSelection.create(state.doc, resolvedPos.pos);
      const tr = state.tr.setSelection(nodeSelection);
      view.dispatch(tr);
      return true;
    } catch (error) {
      console.error("Error setting node selection:", error);
      return false;
    }
  };

  // Format functions
  const applyColor = (color) => {
    if (!editor) return;
    editor.chain().focus().setColor(color).run();
    setCurrentTextColor(color);
    setActiveDropdown(null);
  };

  // Apply custom highlight color
  const applyCustomHighlight = (color) => {
    if (!editor) return;
    editor.chain().focus().setCustomHighlight(color).run(); // Use custom command
    setCurrentHighlightColor(color);
    setActiveDropdown(null);
  };

  // Remove custom highlight
  const removeCustomHighlight = () => {
    if (!editor) return;
    editor.chain().focus().unsetCustomHighlight().run(); // Use custom command
    setActiveDropdown(null);
    setCurrentHighlightColor("#FFFF00"); // Reset indicator to default highlight color
  };

  const applyFontSize = (size) => {
    if (!editor) return;
    editor.chain().focus().setFontSize(`${size}px`).run(); // Corrected template literal
    setSelectedFontSize(size);
    setActiveDropdown(null);
  };

  const applyFontFamily = (family) => {
    if (!editor) return;
    editor.chain().focus().setFontFamily(family).run();
    setSelectedFont(family);
    setActiveDropdown(null);
  };

  // Toggle dropdown visibility
  const toggleDropdown = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  return (
    // Conditionally add the unique instance ID as a class name
    <div
      className={`rich-text-editor-wrapper w-full ${
        isGrayMode ? instanceId : ""
      }`}
    >
      <div className="editor-toolbar">
        {/* Formatting Options */}
        <button
          type="button"
          className={`toolbar-button ${
            editor?.isActive("bold") ? "active" : ""
          }`} // Corrected template literal
          onClick={() => editor?.chain().focus().toggleBold().run()}
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          className={`toolbar-button ${
            editor?.isActive("italic") ? "active" : ""
          }`} // Corrected template literal
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          title="Italic"
        >
          I
        </button>
        <button
          type="button"
          className={`toolbar-button ${
            editor?.isActive("underline") ? "active" : ""
          }`} // Corrected template literal
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
          title="Underline"
        >
          U
        </button>
        <button
          type="button"
          className={`toolbar-button ${
            editor?.isActive("strike") ? "active" : ""
          }`} // Corrected template literal
          onClick={() => editor?.chain().focus().toggleStrike().run()}
          title="Strike Through"
        >
          S
        </button>
        <button
          type="button"
          className={`toolbar-button ${
            editor?.isActive("code") ? "active" : ""
          }`} // Corrected template literal
          onClick={() => editor?.chain().focus().toggleCode().run()}
          title="Code"
        >
          <span style={{ fontFamily: "monospace" }}>&lt;/&gt;</span>
        </button>

        <div className="toolbar-divider"></div>

        {/* Font Family */}
        <div
          className={`dropdown ${activeDropdown === "font" ? "active" : ""}`}
        >
          {" "}
          {/* Corrected template literal */}
          <button
            type="button"
            className="toolbar-button"
            style={{
              minWidth: "120px",
              display: "flex",
              justifyContent: "space-between",
            }}
            onClick={() => toggleDropdown("font")}
          >
            <span style={{ fontFamily: selectedFont }}>{selectedFont}</span>{" "}
            <span>▼</span>
          </button>
          <div className="dropdown-content">
            {activeDropdown === "font" && (
              <span
                className="dropdown-close"
                onClick={() => setActiveDropdown(null)}
              >
                ✕
              </span>
            )}
            {fontOptions.map((font) => (
              <button
                type="button"
                key={font}
                onClick={() => applyFontFamily(font)}
                style={{ fontFamily: font }}
              >
                {font}
              </button>
            ))}
          </div>
        </div>

        <div className="toolbar-divider"></div>

        {/* Text Color */}
        <div
          className={`dropdown ${
            activeDropdown === "textColor" ? "active" : ""
          }`}
        >
          {" "}
          {/* Corrected template literal */}
          <button
            type="button"
            className="toolbar-button"
            title="Text Color"
            onClick={() => toggleDropdown("textColor")}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                className="color-indicator"
                style={{ backgroundColor: currentTextColor }}
              />
              <span>▼</span>
            </div>
          </button>
          <div className="dropdown-content">
            {activeDropdown === "textColor" && (
              <span
                className="dropdown-close"
                onClick={() => setActiveDropdown(null)}
              >
                ✕
              </span>
            )}
            <div className="color-picker">
              {colorOptions.map((color) => (
                <div
                  key={color}
                  className="color-button"
                  style={{ backgroundColor: color }}
                  onClick={() => applyColor(color)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Highlight Color - IMPROVED */}
        <div
          className={`dropdown ${
            activeDropdown === "highlight" ? "active" : ""
          }`}
        >
          {" "}
          {/* Corrected template literal */}
          <button
            type="button"
            className="toolbar-button"
            title="Highlight Color"
            onClick={() => toggleDropdown("highlight")}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                className="color-indicator"
                style={{ backgroundColor: currentHighlightColor }}
              />
              <span>▼</span>
            </div>
          </button>
          <div className="dropdown-content">
            {activeDropdown === "highlight" && (
              <span
                className="dropdown-close"
                onClick={() => setActiveDropdown(null)}
              >
                ✕
              </span>
            )}
            <div className="color-picker">
              <div
                className="color-button"
                style={{
                  backgroundColor: "transparent",
                  position: "relative",
                  borderColor: "#e0e0e0",
                }}
                onClick={removeCustomHighlight} // Use custom remove command
                title="Remove Highlight"
              >
                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "2px",
                    backgroundColor: "#ff0000",
                    transform: "rotate(45deg)",
                  }}
                />
              </div>
              {colorOptions.map((color) => (
                <div
                  key={color}
                  className="color-button"
                  style={{ backgroundColor: color }}
                  onClick={() => applyCustomHighlight(color)} // Use custom apply command
                />
              ))}
            </div>
          </div>
        </div>

        <div className="toolbar-divider"></div>

        {/* Font Size */}
        <div
          className={`dropdown ${
            activeDropdown === "fontSize" ? "active" : ""
          }`}
        >
          {" "}
          {/* Corrected template literal */}
          <button
            type="button"
            className="toolbar-button"
            style={{
              minWidth: "60px",
              display: "flex",
              justifyContent: "space-between",
            }}
            onClick={() => toggleDropdown("fontSize")}
          >
            {selectedFontSize} <span>▼</span>
          </button>
          <div className="dropdown-content">
            {activeDropdown === "fontSize" && (
              <span
                className="dropdown-close"
                onClick={() => setActiveDropdown(null)}
              >
                ✕
              </span>
            )}
            {fontSizes.map((size) => (
              <button
                type="button"
                key={size}
                onClick={() => applyFontSize(size)}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        <div className="toolbar-divider"></div>

        {/* Paragraph Format */}
        <div
          className={`dropdown ${
            activeDropdown === "paragraph" ? "active" : ""
          }`}
        >
          {" "}
          {/* Corrected template literal */}
          <button
            type="button"
            className="toolbar-button"
            style={{
              minWidth: "100px",
              display: "flex",
              justifyContent: "space-between",
            }}
            onClick={() => toggleDropdown("paragraph")}
          >
            {selectedParagraph} <span>▼</span>
          </button>
          <div className="dropdown-content">
            {activeDropdown === "paragraph" && (
              <span
                className="dropdown-close"
                onClick={() => setActiveDropdown(null)}
              >
                ✕
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                // Apply paragraph format
                editor?.chain().focus().setParagraph().run();
                setSelectedParagraph("Paragraph");
                setActiveDropdown(null);
              }}
            >
              Paragraph
            </button>
            <button
              type="button"
              onClick={() => {
                editor?.chain().focus().toggleHeadingStyle(1).run();
                setSelectedParagraph("Heading 1");
                setActiveDropdown(null);
              }}
            >
              Heading 1
            </button>
            <button
              type="button"
              onClick={() => {
                editor?.chain().focus().toggleHeadingStyle(2).run();
                setSelectedParagraph("Heading 2");
                setActiveDropdown(null);
              }}
            >
              Heading 2
            </button>
            <button
              type="button"
              onClick={() => {
                editor?.chain().focus().toggleHeadingStyle(3).run();
                setSelectedParagraph("Heading 3");
                setActiveDropdown(null);
              }}
            >
              Heading 3
            </button>
          </div>
        </div>

        {/* Text Alignment */}
        <button
          type="button"
          className={`toolbar-button ${
            editor?.isActive({ textAlign: "left" }) ? "active" : ""
          }`} // Corrected template literal
          onClick={() => editor?.chain().focus().setTextAlign("left").run()}
          title="Align Left"
        >
          ←
        </button>
        <button
          type="button"
          className={`toolbar-button ${
            editor?.isActive({ textAlign: "center" }) ? "active" : ""
          }`} // Corrected template literal
          onClick={() => editor?.chain().focus().setTextAlign("center").run()}
          title="Align Center"
        >
          ↔
        </button>
        <button
          type="button"
          className={`toolbar-button ${
            editor?.isActive({ textAlign: "right" }) ? "active" : ""
          }`} // Corrected template literal
          onClick={() => editor?.chain().focus().setTextAlign("right").run()}
          title="Align Right"
        >
          →
        </button>

        <div className="toolbar-divider"></div>

        {/* Lists */}
        <button
          type="button"
          className={`toolbar-button ${
            editor?.isActive("bulletList") ? "active" : ""
          }`} // Corrected template literal
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          title="Bullet List"
        >
          • List
        </button>
        <button
          type="button"
          className={`toolbar-button ${
            editor?.isActive("orderedList") ? "active" : ""
          }`} // Corrected template literal
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          title="Ordered List"
        >
          1. List
        </button>

        <div className="toolbar-divider"></div>

        {/* History */}
        <button
          type="button"
          className="toolbar-button"
          onClick={() => editor?.chain().focus().undo().run()}
          title="Undo"
        >
          ↩️
        </button>
        <button
          type="button"
          className="toolbar-button"
          onClick={() => editor?.chain().focus().redo().run()}
          title="Redo"
        >
          ↪️
        </button>
      </div>

      <EditorContent editor={editor} className="editor-content" />
    </div>
  );
};

export default TipTapEditor;
